/**
 * Sortier-/Filterlogik der Historie-Tabelle (`Drawer/HistoryTab/HistoryTab.tsx`, Auftrag
 * "Historie-Tab als sortier-/filterbare Tabelle", 2026-09-06) - Pendant zu `lib/orderList.ts`, aber
 * deutlich kleiner: DREI Spalten (Zeitpunkt/Kategorie/Aktor) statt eines ganzen Board-Spaltensatzes,
 * und ohne dessen Order-Kopplung (kein `Order`/`Supplier`/`StatusDefinitionen`-Argument irgendwo
 * hier). Reine Funktionen, kein React.
 *
 * `Bereichsfilter` (Von/Bis-Datumsbereich) wird aus `orderList.ts` WIEDERVERWENDET, nicht neu
 * definiert - `DatumsBereichFilter.tsx` (das Widget der Zeitpunkt-Spalte) kennt ohnehin nur diesen
 * Typ, siehe dessen Kopfkommentar. Ein eigener, wortgleicher Typ hier waere die Dublette, die der
 * Auftrag an anderer Stelle ausdruecklich vermeiden will ("nicht neu erfunden, sondern dieselben
 * Bausteine wiederverwendet").
 *
 * Die Freitextsuche der Werkzeugleiste (durchsucht `summary`/`feld`/`aktor`) steht ABSICHTLICH NICHT
 * hier: sie braucht den Platzhaltertext `KEINE_BESCHREIBUNG` aus `components/historieDarstellung.ts`
 * fuer den `summary == null`-Fall - ein Import von `components/` nach `lib/` liefe der Schichtung
 * dieses Projekts zuwider (`lib/` importiert nirgends aus `components/`). Sie bleibt deshalb, klein
 * und lokal, in `HistoryTab.tsx` selbst.
 */
import type { Bereichsfilter, OrderListFacet } from "./orderList.ts";
import type { HistorienEintrag } from "../api/historie.ts";
import { localIsoDay } from "./format.ts";

export type { Bereichsfilter };

/**
 * Die sortier-/filterbaren Spalten der Tabelle - "Änderung" (Spalte 3) ist bewusst NICHT dabei: laut
 * Auftrag weder sortierbar noch mit eigenem Spaltenfilter, nur ueber die Freitextsuche der
 * Werkzeugleiste erreichbar.
 */
export type HistorySpalte = "ts" | "kategorie" | "aktor";

export interface HistorySortierung {
  key: HistorySpalte;
  direction: "asc" | "desc";
}

/**
 * Kurzlabel je `kategorie` (Auftrag, Abschnitt "Gewuenschte Tabelle", Spalte 2) - kanonisch, NICHT
 * aus `iconFuer` abgeleitet: `iconFuer` liefert Symbol+Farbklasse, keine Beschriftung.
 */
export const HISTORY_KATEGORIE_LABEL: Record<HistorienEintrag["kategorie"], string> = {
  status: "Status",
  audit: "Vorgang",
  dsv_termin: "Speditionstermin",
  ek_preis: "EK-Preis",
};

/**
 * Die feste, kanonische Optionsliste des Kategorie-Spaltenfilters - alle VIER Werte, unabhaengig
 * davon, ob sie unter den gerade geladenen Eintraegen tatsaechlich vorkommen. Dasselbe Muster wie
 * `orderListStatusOptionen` fuer die Status-Spalte des Boards (kanonisch statt aus den Daten
 * abgeleitet, siehe Auftrag: "wie die Status-Spalte im Board `orderListStatusOptionen` macht, nicht
 * `orderListFacetOptions`").
 */
export function historyKategorieOptionen(): OrderListFacet[] {
  return (Object.keys(HISTORY_KATEGORIE_LABEL) as HistorienEintrag["kategorie"][]).map((kategorie) => ({
    value: kategorie,
    label: HISTORY_KATEGORIE_LABEL[kategorie],
  }));
}

/**
 * Die Optionsliste des Aktor-Spaltenfilters - im Unterschied zur Kategorie-Liste oben AUS DEN
 * TATSAECHLICH GELADENEN Eintraegen DIESER Bestellung abgeleitet (Set aus `aktor ?? "System"`,
 * alphabetisch), nicht kanonisch und nicht global - siehe Auftrag, Spalte 4.
 */
export function historyAktorOptionen(eintraege: readonly HistorienEintrag[]): OrderListFacet[] {
  const werte = new Set<string>();
  for (const eintrag of eintraege) werte.add(eintrag.aktor ?? "System");
  return [...werte]
    .sort((a, b) => a.localeCompare(b, "de", { numeric: true, sensitivity: "base" }))
    .map((wert) => ({ value: wert, label: wert }));
}

/**
 * Der Sortierwert eines Eintrags je Spalte - `ts` bleibt der rohe ISO-Zeitstempel (lexikalisch
 * sortierbar, dieselbe Eigenheit wie `orderListFacet(...).value` bei den Datumsspalten des Boards).
 */
export function historySortValue(eintrag: HistorienEintrag, spalte: HistorySpalte): string {
  switch (spalte) {
    case "ts":
      return eintrag.ts;
    case "kategorie":
      return HISTORY_KATEGORIE_LABEL[eintrag.kategorie];
    case "aktor":
      return eintrag.aktor ?? "System";
  }
}

/**
 * Die drei Spaltenfilter der Tabelle - Mehrfachauswahl (ODER-verknuepft) fuer Kategorie/Aktor, ein
 * Von/Bis-Bereich fuer Zeitpunkt. Alle drei Teile sind UND-verknuepft untereinander (dieselbe
 * Semantik wie `Spaltenfilter` im Board).
 */
export interface HistoryFilter {
  kategorie: readonly string[];
  aktor: readonly string[];
  zeitraum: Bereichsfilter;
}

/**
 * `true`, wenn der Zeitraum-Spaltenfilter (Spalte "Zeitpunkt") tatsaechlich einschraenkt - eigene,
 * exportierte Funktion statt eines an zwei Stellen wortgleich wiederholten Ausdrucks
 * (Pruefer-Befund "Gering", Nachbesserung 2026-09-06: dieselbe Zeile stand zuvor sowohl hier als
 * auch, wortgleich, in `HistoryTab.tsx` fuer die `aktiv`-Markierung des Spaltenkopfs selbst).
 */
export function zeitraumFilterAktiv(zeitraum: Bereichsfilter): boolean {
  return Boolean(zeitraum.von || zeitraum.bis || zeitraum.ohneWert);
}

/**
 * `true`, wenn irgendeiner der drei Spaltenfilter tatsaechlich einschraenkt - Grundlage fuer den
 * Knopf "Filter zuruecksetzen" (`HistoryTab.tsx` verknuepft das ODER-verknuepft mit der
 * Freitextsuche, die hier bewusst nicht mitgezaehlt wird, siehe Kopfkommentar oben - der Knopf muss
 * erscheinen, sobald EINER von beiden greift, nicht erst wenn BEIDE gleichzeitig greifen).
 */
export function historyFilterAktiv(filter: HistoryFilter): boolean {
  return filter.kategorie.length > 0 || filter.aktor.length > 0 || zeitraumFilterAktiv(filter.zeitraum);
}

/**
 * `true`, wenn `eintrag` den gesetzten Spaltenfilter erfuellt - die Freitextsuche der Werkzeugleiste
 * ist NICHT Teil davon (siehe Kopfkommentar). `von`/`bis` vergleichen den Tag von `ts` in DERSELBEN
 * ORTSZEIT-Darstellung, die die Tabellenzelle zeigt (`fmtDT`/`localIsoDay`, `lib/format.ts`) - NICHT
 * die ersten zehn Zeichen des rohen ISO-UTC-Zeitstempels (Pruefer-Befund "Wichtig" 1, Nachbesserung
 * 2026-09-06: ein `ts` wie "2026-09-02T22:15:00.000Z" steht in der Zelle als "03.09.26" (MESZ +2h),
 * ein UTC-Schnitt haette ihn faelschlich unter "02.09." einsortiert). Die Uhrzeit selbst bleibt
 * weiterhin unberuecksichtigt, wie der Auftrag es fuer die Zeitpunkt-Spalte verlangt.
 *
 * `ohneWert` liegt hier IMMER auf `false`: `ts` ist nach dem API-Typ nie leer (anders als die
 * Datumsspalten des Boards, die `orderListFacet` als `"__EMPTY__"` markieren kann), der Zustand "nur
 * Zeilen ohne Zeitpunkt" kann es deshalb nie geben. Die Checkbox "Nur ohne Wert" - die
 * `DatumsBereichFilter` sonst UNGEFRAGT mitliefert - blendet `HistoryTab.tsx` fuer die Zeitpunkt-
 * Spalte deshalb per `ohneWertVerstecken` aus (Pruefer-Befund "Wichtig" 2: ohne das haette ein Klick
 * darauf JEDE Zeile ausgeblendet, weil `if (ohneWert) return false` unten dann fuer ausnahmslos
 * jeden Eintrag griffe). Der Zweig bleibt trotzdem stehen - reine Verteidigung gegen einen Zustand,
 * den die Oberflaeche nicht mehr anbietet, kein `Bereichsfilter`-Feld eigens dafuer entfernt.
 */
export function historyZeilePasstFilter(eintrag: HistorienEintrag, filter: HistoryFilter): boolean {
  if (filter.kategorie.length && !filter.kategorie.includes(eintrag.kategorie)) return false;
  if (filter.aktor.length && !filter.aktor.includes(eintrag.aktor ?? "System")) return false;
  const { von, bis, ohneWert } = filter.zeitraum;
  if (ohneWert) return false;
  const tag = localIsoDay(eintrag.ts);
  if (von && tag < von) return false;
  if (bis && tag > bis) return false;
  return true;
}
